import {
  ApiClient,
  type CvrApiCompanyListItemDto
} from "../../types/domainobjects";
import type { CvrCompanyPageResponse } from "../types/cvr-company-page-response";
import type { CvrCompanyQuery } from "../types/cvr-company-query";
import type { CvrCompanyRow } from "../types/cvr-company-row";
import type { CvrSortBy } from "../types/cvr-sort-by";
import type { CvrSortDirection } from "../types/cvr-sort-direction";
import type { CvrFilters } from "../types/cvr-filters";

const apiClient = new ApiClient();

const mapRows = (items: CvrApiCompanyListItemDto[]): CvrCompanyRow[] => {
  return items.map((item) => {
    const createdDateValue = item.createdDate instanceof Date
      ? item.createdDate
      : item.createdDate
        ? new Date(item.createdDate)
        : null;

    return {
      id: item.id ?? 0,
      cvrNumber: item.cvrNumber ?? 0,
      name: item.name ?? null,
      createdDate: createdDateValue && !Number.isNaN(createdDateValue.getTime())
        ? createdDateValue.toISOString()
        : null,
      reklamebeskyttet: item.reklamebeskyttet ?? false,
      beliggenhedsadresse: item.beliggenhedsadresse ?? null,
      nyesteHovedbranche: item.nyesteHovedbranche ?? null,
      otherBrancheCodes: (item as unknown as { otherBrancheCodes?: string | null }).otherBrancheCodes ?? null,
      antalAnsatte: item.antalAnsatte ?? null,
      virksomhedsform: item.virksomhedsform ?? null,
      websiteLookupFailed: item.websiteLookupFailed ?? false,
      website: item.website ?? null,
      desktopPerformanceScore: item.desktopPerformanceScore ?? null,
      desktopAccessibilityScore: item.desktopAccessibilityScore ?? null,
      desktopBestPracticesScore: item.desktopBestPracticesScore ?? null,
      desktopSeoScore: item.desktopSeoScore ?? null,
      desktopSpeedIndexMs: item.desktopSpeedIndexMs ?? null,
      desktopFirstContentfulPaintMs: item.desktopFirstContentfulPaintMs ?? null,
      desktopLargestContentfulPaintMs: item.desktopLargestContentfulPaintMs ?? null,
      desktopTotalBlockingTimeMs: item.desktopTotalBlockingTimeMs ?? null,
      desktopCumulativeLayoutShift: item.desktopCumulativeLayoutShift ?? null,
      desktopStatus: item.desktopStatus ?? null,
      desktopErrorMessage: item.desktopErrorMessage ?? null,
      desktopScreenshotDataUrl: item.desktopScreenshotDataUrl ?? null
    };
  });
};

export const getPagedCvrCompanies = async (
  query: CvrCompanyQuery
): Promise<CvrCompanyPageResponse> => {
  const searchPayload = JSON.stringify(buildSearchPayload(query.searchTerm, query.filters));

  const response = await apiClient.cvrApiCompany_Get(
    query.page,
    query.pageSize,
    query.sortBy,
    query.sortDirection,
    searchPayload
  );

  return {
    items: mapRows(response.items ?? []),
    totalCount: response.totalCount ?? 0,
    page: response.page ?? query.page,
    pageSize: response.pageSize ?? query.pageSize
  };
};

export const exportCvrCompaniesExcel = async (
  sortBy: CvrSortBy,
  sortDirection: CvrSortDirection,
  searchTerm: string | null,
  filters: CvrFilters
): Promise<void> => {
  const searchPayload = JSON.stringify(buildSearchPayload(searchTerm, filters));
  const encodedSortBy = encodeURIComponent(String(sortBy));
  const encodedSortDirection = encodeURIComponent(String(sortDirection));
  const encodedSearchTerm = encodeURIComponent(searchPayload);
  const clientInternals = apiClient as unknown as {
    baseUrl: string;
    http: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> };
  };
  const url = `${clientInternals.baseUrl}/api/cvr-companies/export-excel?sortBy=${encodedSortBy}&sortDirection=${encodedSortDirection}&searchTerm=${encodedSearchTerm}`;
  const response = await clientInternals.http.fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    }
  });

  if (!response.ok) {
    throw new Error("Failed to export Excel.");
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get("content-disposition");
  const fileName = parseFileName(contentDisposition) ?? "cvr-export.xlsx";

  if (!blob) {
    throw new Error("No file received from server.");
  }

  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(objectUrl);
};

const buildSearchPayload = (searchTerm: string | null, filters: CvrFilters) => {
  return {
    text: searchTerm?.trim() || null,
    minEmployees: filters.minEmployees,
    maxEmployees: filters.maxEmployees,
    createdFrom: filters.createdFrom,
    createdTo: filters.createdTo,
    companyForms: filters.companyForms,
    reklamebeskyttet: filters.reklamebeskyttet,
    hasWebsite: filters.hasWebsite
  };
};

const parseFileName = (contentDisposition: string | null): string | null => {
  if (!contentDisposition) {
    return null;
  }

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const defaultMatch = /filename="?([^"]+)"?/i.exec(contentDisposition);
  return defaultMatch?.[1] ?? null;
};
